// types.ts

export type IndustryKey =
  | "ecommerce"
  | "saas"
  | "real-estate"
  | "healthcare"
  | "travel"
  | "education"
  | "automotive"
  | "food-beverage";

export interface IndustryBenchmark {
  name: string;
  min: number;
  max: number;
  recommendations: string[];
}

export interface FormInputs {
  totalRevenue: number;
  adSpend: number;
  industry: IndustryKey;
  conversionRate?: number;
  costPerLead?: number;
  customerLifetimeValue?: number;
  customerAcquisitionCost?: number;
}

export const INDUSTRY_BENCHMARKS: Record<IndustryKey, IndustryBenchmark> = {
  ecommerce: {
    name: "E-commerce",
    min: 2.5,
    max: 4.0,
    recommendations: [
      "Optimize product descriptions and images to boost engagement",
      "Implement retargeting campaigns to recover abandoned carts",
      "Offer personalized discounts or bundle deals to improve CRO",
      "Focus on high-converting product categories",
      "Improve shopping cart experience",
    ],
  },
  saas: {
    name: "SaaS",
    min: 3.0,
    max: 6.0,
    recommendations: [
      "Focus on feature education and value proposition",
      "Implement free trial or freemium model",
      "Create targeted content for different user segments",
      "Optimize onboarding experience",
      "Use case studies and social proof",
    ],
  },
  "real-estate": {
    name: "Real Estate",
    min: 4.0,
    max: 7.0,
    recommendations: [
      "Focus on high-value leads by targeting specific demographics",
      "Use video ads showcasing properties to increase engagement",
      "Offer free consultations or virtual tours",
      "Implement advanced property search filters",
      "Create neighborhood guides and market reports",
    ],
  },
  healthcare: {
    name: "Healthcare",
    min: 3.5,
    max: 5.5,
    recommendations: [
      "Use location-based targeting for healthcare services",
      "Focus on educational content to build trust",
      "Optimize for mobile users to increase lead generation",
      "Implement telemedicine options",
      "Showcase patient testimonials",
    ],
  },
  travel: {
    name: "Travel & Hospitality",
    min: 2.0,
    max: 3.0,
    recommendations: [
      "Create seasonal promotion campaigns",
      "Implement early booking discounts",
      "Use high-quality destination imagery",
      "Offer package deals and upgrades",
      "Focus on mobile booking experience",
    ],
  },
  education: {
    name: "Education",
    min: 3.0,
    max: 5.0,
    recommendations: [
      "Create program-specific landing pages",
      "Implement lead nurturing campaigns",
      "Showcase student success stories",
      "Offer virtual campus tours",
      "Create informative course previews",
    ],
  },
  automotive: {
    name: "Automotive",
    min: 3.0,
    max: 5.0,
    recommendations: [
      "Implement virtual showroom experiences",
      "Create detailed vehicle comparison tools",
      "Offer test drive scheduling",
      "Use targeted local advertising",
      "Showcase financing options",
    ],
  },
  "food-beverage": {
    name: "Food & Beverage",
    min: 2.5,
    max: 4.0,
    recommendations: [
      "Use high-quality food photography",
      "Implement loyalty programs",
      "Create time-sensitive promotions",
      "Focus on local targeting",
      "Showcase menu variety and specials",
    ],
  },
};
